import { useState, useEffect } from 'react';
import { Key, Save, Eye, EyeOff, ShieldAlert, CheckCircle, AlertCircle, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../api/api';

// Интерфейс для списка бирж
interface ExchangeOption {
  id: string;
  name: string;
  status: 'ACTIVE' | 'SOON' | 'DEV';
  color: string;
}

export const ApiSettings = () => {
  const { t } = useTranslation();

  // Состояние
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [exchange, setExchange] = useState('binance');

  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [msg, setMsg] = useState('');

  const [savedSettings, setSavedSettings] = useState<any>(null);

  // Список бирж
  const exchanges: ExchangeOption[] = [
    { id: 'binance', name: 'Binance', status: 'ACTIVE', color: 'from-yellow-500 to-yellow-600' },
    { id: 'bybit', name: 'Bybit', status: 'ACTIVE', color: 'from-orange-500 to-orange-600' },
    { id: 'okx', name: 'OKX', status: 'SOON', color: 'from-slate-700 to-slate-600' },
    { id: 'bitget', name: 'Bitget', status: 'SOON', color: 'from-cyan-600 to-blue-600' },
    { id: 'kucoin', name: 'KuCoin', status: 'DEV', color: 'from-green-600 to-emerald-600' },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (!savedSettings) return;
    if (exchange === savedSettings.exchange) {
      setApiKey(savedSettings.apiKey || '');
      setApiSecret(savedSettings.apiSecret ? '••••••••••••••••' : '');
    } else {
      setApiKey('');
      setApiSecret('');
    }
  }, [exchange, savedSettings]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/users/settings');
      if (res.data) {
        setSavedSettings(res.data);
        setExchange(res.data.exchange || 'binance');
      }
    } catch (e) {
      console.error("Error loading settings", e);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setStatus('idle');
    try {
      const payload: any = { exchange, apiKey };
      if (apiSecret && !apiSecret.includes('••••')) {
        payload.apiSecret = apiSecret;
      }
      await api.post('/users/settings', payload);
      setStatus('success');
      setMsg(`${t('platform.apiSettings.success')} ${exchange.toUpperCase()}!`);

      setSavedSettings((prev: any) => ({
        ...prev,
        exchange,
        apiKey,
        apiSecret: apiSecret.includes('••••') ? prev?.apiSecret : 'exists'
      }));
      setTimeout(() => setStatus('idle'), 3000);
    } catch (e: any) {
      setStatus('error');
      setMsg(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto animate-in fade-in duration-500">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">{t('platform.apiSettings.title')}</h2>
        <p className="text-slate-400">Manage your exchange connections securely.</p>
      </div>

      {/* Уведомления */}
      {status === 'success' && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-2xl flex items-center gap-3 text-green-400">
          <CheckCircle size={20} /> {msg}
        </div>
      )}
      {status === 'error' && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-400">
          <AlertCircle size={20} /> {msg}
        </div>
      )}

      {/* GRID ВЫБОРА БИРЖИ */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {exchanges.map((ex) => (
          <button
            key={ex.id}
            disabled={ex.status !== 'ACTIVE'}
            onClick={() => setExchange(ex.id)}
            className={`
              relative p-4 rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center gap-3 h-32 group overflow-hidden
              ${exchange === ex.id && ex.status === 'ACTIVE'
                ? 'bg-slate-800 border-blue-500 ring-2 ring-blue-500/20 shadow-lg shadow-blue-500/10'
                : 'bg-slate-900/50 border-slate-800 hover:border-slate-600'
              }
              ${ex.status !== 'ACTIVE' ? 'opacity-50 cursor-not-allowed grayscale' : 'cursor-pointer'}
            `}
          >
            {/* Иконка с градиентом */}
            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${ex.color} flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform`}>
              <span className="font-bold text-white text-xs">{ex.name.substring(0, 2).toUpperCase()}</span>
            </div>

            <span className={`font-bold text-sm ${exchange === ex.id ? 'text-white' : 'text-slate-400'}`}>
              {ex.name}
            </span>

            {/* Активный статус */}
            {exchange === ex.id && ex.status === 'ACTIVE' && (
              <div className="absolute top-2 right-2">
                <CheckCircle size={16} className="text-blue-500" />
              </div>
            )}

            {/* Бейдж SOON */}
            {ex.status === 'SOON' && (
              <div className="absolute inset-0 bg-slate-950/60 flex items-center justify-center backdrop-blur-[1px]">
                <span className="bg-slate-800 text-slate-300 text-[10px] font-bold px-2 py-1 rounded border border-slate-600 shadow-xl">SOON</span>
              </div>
            )}

            {/* Бейдж DEV */}
            {ex.status === 'DEV' && (
              <div className="absolute inset-0 bg-slate-950/60 flex items-center justify-center backdrop-blur-[1px]">
                <span className="bg-blue-900/80 text-blue-300 text-[10px] font-bold px-2 py-1 rounded border border-blue-800 flex items-center gap-1 shadow-xl">
                  <Lock size={10} /> DEV
                </span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* ФОРМА ВВОДА (Только для активных бирж) */}
      <div className="bg-[#1e293b] border border-slate-800 rounded-[32px] p-8 shadow-xl mb-8 relative overflow-hidden">
        <div className={`absolute top-0 left-0 w-2 h-full bg-gradient-to-b ${exchanges.find(e => e.id === exchange)?.color}`}></div>

        <div className="flex items-center gap-3 mb-6">
          <div className="bg-slate-800 p-2 rounded-lg border border-slate-700"><Key className="text-slate-400" size={24} /></div>
          <div>
            <h3 className="text-xl font-bold text-white">{t('platform.apiSettings.keysTitle')}</h3>
            <p className="text-slate-500 text-sm">
              Configuring <span className="text-white font-bold uppercase">{exchange}</span> connection.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-2 block tracking-wider">{t('platform.apiSettings.apiKey')}</label>
            <input
              type="text"
              className="w-full bg-[#0f172a] border border-slate-700 rounded-xl px-4 py-3 text-white font-mono focus:border-blue-500 outline-none transition-all placeholder:text-slate-600"
              placeholder={`${t('platform.apiSettings.placeholderKey')} ${exchange}`}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-2 block tracking-wider">{t('platform.apiSettings.apiSecret')}</label>
            <div className="relative">
              <input
                type={showSecret ? "text" : "password"}
                className="w-full bg-[#0f172a] border border-slate-700 rounded-xl px-4 py-3 text-white font-mono focus:border-blue-500 outline-none transition-all pr-12 placeholder:text-slate-600"
                placeholder={apiSecret.includes('•••') ? t('platform.apiSettings.placeholderSecretHidden') : `${t('platform.apiSettings.placeholderSecret')} ${exchange}`}
                value={apiSecret}
                onChange={e => setApiSecret(e.target.value)}
              />
              <button onClick={() => setShowSecret(!showSecret)} className="absolute right-4 top-3.5 text-slate-500 hover:text-white transition-colors">
                {showSecret ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {apiSecret.includes('•••') && (
              <p className="text-[10px] text-green-500 mt-2 ml-1 flex items-center gap-1 font-bold">
                <CheckCircle size={12} /> {t('platform.apiSettings.keySaved')}
              </p>
            )}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-700/50 flex flex-col md:flex-row items-center gap-4">
          <div className="flex-1 text-xs text-slate-500 flex gap-2">
            <ShieldAlert size={16} className="text-yellow-500 shrink-0" />
            <p>We encrypt your keys with <strong>AES-256</strong>. Ensure you enabled <strong>Futures Trading</strong> permissions and disabled <strong>Withdrawals</strong>.</p>
          </div>
          <button
            onClick={handleSave}
            disabled={loading}
            className="w-full md:w-auto px-8 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 active:scale-95"
          >
            {loading ? t('platform.apiSettings.saving') : <><Save size={20} /> {t('platform.apiSettings.saveBtn')}</>}
          </button>
        </div>
      </div>

      {/* Current Settings Info */}
      {savedSettings && savedSettings.apiKey && (
        <div className="bg-slate-900/30 border border-slate-800 rounded-2xl p-4 flex items-center justify-between text-sm">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${savedSettings.exchange === exchange ? 'bg-green-500 animate-pulse' : 'bg-slate-600'}`}></div>
            <span className="text-slate-400">Current Active: <span className="text-white font-bold uppercase">{savedSettings.exchange}</span></span>
          </div>
          <span className="font-mono text-slate-500">ID: ...{savedSettings.apiKey.slice(-4)}</span>
        </div>
      )}
    </div>
  );
};